/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { RouterProps, showNotification } from '../App';
import { UserFeature, UserFeatureEnum } from '../types/UserFeature';
import { client } from '..';
import gql from 'graphql-tag';
import { IntegrationSettingsNamesData, retrieveIntegrationSettingsNamesQueryData, SettingName } from './IntegrationConfigurationRepository';
import InnographyTab from './tabs/InnographyTab';
import VisionSixTab from './tabs/VisionSixTab';
import ExternalAPIConfigurationTab from './tabs/ExternalAPIConfigurationTab';
import CalendlyTab from './tabs/CalendlyTab';
import { CalendlyLogo } from '../v2/common/svg';
import InsertLinkOutlinedIcon from '@material-ui/icons/InsertLinkOutlined';
import StoreMallDirectoryOutlinedIcon from '@material-ui/icons/StoreMallDirectoryOutlined';
import SettingsEthernetIcon from '@material-ui/icons/SettingsEthernet';
import ExtensionIcon from '@material-ui/icons/Extension';
import VpnKeyOutlinedIcon from '@material-ui/icons/VpnKeyOutlined';
import { FilteredVerticalTabPage, SidebarItem, SidebarGroup } from '../v2/layouts/FilteredVerticalTabPage';
import { SkeletonLoader } from '../components/loading/SkeletonLoader';

export enum IntegrationConfigurationList {
    InnographyConfiguration = 0,
    Vision6Configuration,
    ExternalAPIConfiguration,
    CalendlyConfiguration,
}

export interface SettingNotification {
    description: string;
    id: number;
    message: string;
    status: boolean;
}

interface IntegrationConfigurationPageState {
    subscriptionResponse: SettingNotification | null;
    tabs: SettingName[];
    isTabLoading: boolean;
}

interface IntegrationConfigurationPageProps extends RouterProps {}

const INTEGRATION_GROUPS: SidebarGroup[] = [
    {
        key: 'integrations',
        label: 'Third-Party Services',
        icon: <ExtensionIcon />,
        iconColor: '#1565c0',
    },
    {
        key: 'internal',
        label: 'Internal API',
        icon: <VpnKeyOutlinedIcon />,
        iconColor: '#7b1fa2',
    },
];

const INTEGRATION_ORDER: string[] = [
    IntegrationConfigurationList[IntegrationConfigurationList.CalendlyConfiguration],
    IntegrationConfigurationList[IntegrationConfigurationList.InnographyConfiguration],
    IntegrationConfigurationList[IntegrationConfigurationList.Vision6Configuration],
    IntegrationConfigurationList[IntegrationConfigurationList.ExternalAPIConfiguration],
];

// tslint:disable-next-line: no-any
const INTEGRATION_META: Record<string, { icon: React.ReactNode; iconBg: string; iconColor: string }> = {
    InnographyConfiguration: {
        icon: <InsertLinkOutlinedIcon fontSize="small" />,
        iconBg: '#e3f2fd',
        iconColor: '#1565c0',
    },
    Vision6Configuration: {
        icon: <StoreMallDirectoryOutlinedIcon fontSize="small" />,
        iconBg: '#e8f5e9',
        iconColor: '#2e7d32',
    },
    ExternalAPIConfiguration: {
        icon: <SettingsEthernetIcon fontSize="small" />,
        iconBg: '#f3e5f5',
        iconColor: '#7b1fa2',
    },
    CalendlyConfiguration: {
        icon: <CalendlyLogo size={18} />,
        iconBg: '#e8f0fe',
        iconColor: '#006bff',
    },
};

const isHasAccess = (internalName: string): boolean => {
    if (internalName === IntegrationConfigurationList[IntegrationConfigurationList.InnographyConfiguration]) {
        return UserFeature.HasAccess(UserFeatureEnum.hasInnographyLink);
    }
    if (internalName === IntegrationConfigurationList[IntegrationConfigurationList.Vision6Configuration]) {
        return UserFeature.HasAccess(UserFeatureEnum.hasVisionSix);
    }
    if (internalName === IntegrationConfigurationList[IntegrationConfigurationList.CalendlyConfiguration]) {
        return UserFeature.HasAccess(UserFeatureEnum.hasCalendlyIntegration);
    }
    return true;
};

export default function IntegrationConfigurationPage(props: IntegrationConfigurationPageProps) {

    const [pageState, setPageState] = useState<IntegrationConfigurationPageState>({
        subscriptionResponse: null,
        tabs: [],
        isTabLoading: true,
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchTabData();
        // tslint:disable-next-line: align
    }, []);

    useEffect(() => {
        const sub = subscribeSettingNotification();
        return () => { sub.unsubscribe(); };
        // tslint:disable-next-line: align
    }, []);

    const subscribeSettingNotification = () => {
        return client
            .subscribe({
                fetchPolicy: 'no-cache',
                query: SettingNotificationSubscription,
                variables: {},
            })
            .subscribe({
                // tslint:disable-next-line: no-any
                next(results: any) {
                    setPageState(prev => ({
                        ...prev,
                        subscriptionResponse: results.data.settingNotification,
                    }));
                    if (results.data.settingNotification.status) {
                        showNotification('Success', results.data.settingNotification.message, 'info');
                    } else {
                        showNotification('Failed', results.data.settingNotification.message, 'error');
                    }
                },
                // tslint:disable-next-line: no-any
                error(err: any) {
                    // tslint:disable-next-line: no-console
                    console.error(err);
                    showNotification(null, err, 'error');
                },
            });
    };

    const fetchTabData = () => {
        retrieveIntegrationSettingsNamesQueryData(
            true,
            (data: IntegrationSettingsNamesData) => {
                setPageState(prev => ({
                    ...prev,
                    tabs: data.settings.integrations.settingNames.settingName,
                    isTabLoading: false,
                }));
            },
            // tslint:disable-next-line: no-any
            (reason: any) => {
                showNotification(null, reason, 'error');
                setPageState(prev => ({ ...prev, isTabLoading: false }));
            },
        );
    };

    const sidebarItems: SidebarItem[] = useMemo(
        () => pageState.tabs
            .filter(tab => isHasAccess(tab.internalName))
            .map(tab => {
                const meta = INTEGRATION_META[tab.internalName] ?? {
                    icon: <InsertLinkOutlinedIcon fontSize="small" />,
                    iconBg: '#f3f4f6',
                    iconColor: '#6b7280',
                };
                const isInternal = tab.internalName === IntegrationConfigurationList[IntegrationConfigurationList.ExternalAPIConfiguration];
                return {
                    id: tab.internalName,
                    name: tab.title,
                    description: tab.description,
                    group: isInternal ? 'internal' : 'integrations',
                    ...meta,
                };
            })
            .sort((a, b) => {
                const ai = INTEGRATION_ORDER.indexOf(a.id);
                const bi = INTEGRATION_ORDER.indexOf(b.id);
                return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
            }),
        [pageState.tabs]
    );

    const defaultSelectedId = props.match.params.tab
        || IntegrationConfigurationList[IntegrationConfigurationList.InnographyConfiguration];

    const onItemSelect = (item: SidebarItem) => {
        props.history.push(`/integrationConfiguration/${item.id}`);
    };

    return (
        <FilteredVerticalTabPage
            icon={<ExtensionIcon fontSize="small" />}
            title="Integration Configuration"
            subtitle="Manage third-party service integrations"
            onBack={() => props.history.push('/configurationDashboard')}
            backTooltip="Back to Configuration"
            enableSearch={true}
            searchPlaceholder="Search integrations…"
            sidebarGroups={INTEGRATION_GROUPS}
            sidebarItems={sidebarItems}
            defaultSelectedId={defaultSelectedId}
            sidebarLoading={pageState.isTabLoading}
            onItemSelect={onItemSelect}
            renderContent={(selectedItem) => {
                if (pageState.isTabLoading) {
                    return <SkeletonLoader variant="form" rows={4} columns={2} />;
                }
                if (!selectedItem) {
                    return null;
                }
                if (
                    selectedItem.id === IntegrationConfigurationList[IntegrationConfigurationList.InnographyConfiguration] &&
                    UserFeature.HasAccess(UserFeatureEnum.hasInnographyLink)
                ) {
                    return <InnographyTab subscriptionResponse={pageState.subscriptionResponse} />;
                }
                if (
                    selectedItem.id === IntegrationConfigurationList[IntegrationConfigurationList.Vision6Configuration] &&
                    UserFeature.HasAccess(UserFeatureEnum.hasVisionSix)
                ) {
                    return <VisionSixTab subscriptionResponse={pageState.subscriptionResponse} />;
                }
                if (selectedItem.id === IntegrationConfigurationList[IntegrationConfigurationList.ExternalAPIConfiguration]) {
                    return <ExternalAPIConfigurationTab subscriptionResponse={pageState.subscriptionResponse} />;
                }
                if (
                    selectedItem.id === IntegrationConfigurationList[IntegrationConfigurationList.CalendlyConfiguration] &&
                    UserFeature.HasAccess(UserFeatureEnum.hasCalendlyIntegration)
                ) {
                    return <CalendlyTab />;
                }
                return null;
            }}
        />
    );
}

const SettingNotificationSubscription = gql`
    subscription SettingfigNotification {
        settingNotification {
            description
            id
            message
            status
        }
    }
`;
